import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/Shared/PageHeader';
import SharedButton from '../../components/Shared/SharedButton';
import CustomTable from '../../components/Shared/CustomTable';
import TableLoading from '../../components/Shared/TableLoading';
import { getERPClient } from '../../services/supplyChainApi';
import SkeletonCardLoading from '../../components/Shared/SkeletonCardLoading';
import toast from 'react-hot-toast';

export default function ERPPurchasingDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalSuppliers: 0,
    sfdaSuppliers: 0,
    totalItems: 0,
    activeItems: 0,
    totalInvoices: 0,
    pendingVatAmount: 0,
    totalProcurementValue: 0,
  });
  const [recentInvoices, setRecentInvoices] = useState([]);

  useEffect(() => {
    async function fetchDashboard() {
      setLoading(true);
      try {
        const erpClient = getERPClient();
        const [, supRes, itemRes, invRes] = await Promise.all([
          erpClient.getPurchasingDashboard().catch(() => null),
          erpClient.getSuppliers({ limit: 200 }).catch(() => ({ data: [] })),
          erpClient.getItems({ limit: 200 }).catch(() => ({ data: [] })),
          erpClient.getPurchaseInvoices({ limit: 10 }).catch(() => ({ data: [] })),
        ]);

        const supList = Array.isArray(supRes) ? supRes : (supRes?.data || supRes?.suppliers || []);
        const itemList = Array.isArray(itemRes) ? itemRes : (itemRes?.data || itemRes?.items || []);
        const invList = Array.isArray(invRes) ? invRes : (invRes?.data || invRes?.purchase_invoices || invRes?.invoices || []);

        const sfdaCount = supList.filter((s) => s.sfda_licensed).length;
        const activeItemCount = itemList.filter((i) => i.is_active).length;

        const totalValue = invList.reduce((acc, inv) => acc + (Number(inv.total_amount) || 0), 0);
        const vatValue = invList.reduce((acc, inv) => acc + (Number(inv.total_vat_amount) || 0), 0);

        setStats({
          totalSuppliers: supList.length,
          sfdaSuppliers: sfdaCount,
          totalItems: itemList.length,
          activeItems: activeItemCount,
          totalInvoices: invList.length,
          pendingVatAmount: vatValue,
          totalProcurementValue: totalValue,
        });

        setRecentInvoices(invList.slice(0, 5));
      } catch (err) {
        console.error('Failed to load ERP purchasing dashboard:', err);
        toast.error(t('Failed to load purchasing dashboard'));
      } finally {
        setLoading(false);
      }
    }

    fetchDashboard();
  }, [t]);

  const columnsData = [
    { field: 'invoice_number', header: t('Invoice #'), style: { width: '150px' } },
    { field: 'supplier_name', header: t('Supplier') },
    { field: 'issue_date', header: t('Issue Date'), style: { width: '120px' } },
    {
      body: (inv) => (
        <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${
          inv.status === 'PAID' ? 'bg-green-100 text-green-800' :
          inv.status === 'CANCELLED' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
        }`}>
          {inv.status || 'ISSUED'}
        </span>
      ),
      field: 'status',
      header: t('Status'),
      style: { width: '110px' }
    },
    {
      body: (inv) => `SAR ${Number(inv.total_amount || 0).toFixed(2)}`,
      field: 'total_amount',
      header: t('Total Amount'),
      style: { width: '140px' }
    },
  ];

  return (
    <div className="p-4 space-y-5">
      <PageHeader
        title={t('Supply Chain ERP Dashboard')}
        showBack={false}
      />

      {/* Action shortcuts */}
      <div className="flex flex-wrap gap-3">
        <SharedButton
          label={t('New Purchase Invoice')}
          icon="pi pi-file-plus"
          onClick={() => navigate('/manager/erp/purchase-invoices/create')}
          buttonType="primary"
        />
        <SharedButton
          label={t('Suppliers Directory')}
          icon="pi pi-users"
          onClick={() => navigate('/manager/erp/suppliers')}
          buttonType="default"
        />
        <SharedButton
          label={t('Items Master Catalog')}
          icon="pi pi-box"
          onClick={() => navigate('/manager/erp/items')}
          buttonType="default"
        />
      </div>

      {loading ? (
        <SkeletonCardLoading rows={4} />
      ) : (
        <>
          {/* Stat Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-blue-500">
              <div className="flex justify-between items-center text-gray-500 text-xs font-semibold uppercase">
                <span>{t('Active Suppliers')}</span>
                <i className="pi pi-users text-blue-500 text-lg"></i>
              </div>
              <div className="mt-2 text-2xl font-bold text-gray-900">{stats.totalSuppliers}</div>
              <div className="text-xs text-blue-600 font-medium mt-1">
                {stats.sfdaSuppliers} {t('SFDA Licensed')}
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-emerald-500">
              <div className="flex justify-between items-center text-gray-500 text-xs font-semibold uppercase">
                <span>{t('Master Items')}</span>
                <i className="pi pi-box text-emerald-500 text-lg"></i>
              </div>
              <div className="mt-2 text-2xl font-bold text-gray-900">{stats.totalItems}</div>
              <div className="text-xs text-emerald-600 font-medium mt-1">
                {stats.activeItems} {t('Active Catalog Items')}
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-amber-500">
              <div className="flex justify-between items-center text-gray-500 text-xs font-semibold uppercase">
                <span>{t('Total Invoices')}</span>
                <i className="pi pi-file-check text-amber-500 text-lg"></i>
              </div>
              <div className="mt-2 text-2xl font-bold text-gray-900">{stats.totalInvoices}</div>
              <div className="text-xs text-amber-600 font-medium mt-1">
                {t('Recorded Procurement Entries')}
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-indigo-500">
              <div className="flex justify-between items-center text-gray-500 text-xs font-semibold uppercase">
                <span>{t('Procurement Volume')}</span>
                <i className="pi pi-dollar text-indigo-500 text-lg"></i>
              </div>
              <div className="mt-2 text-2xl font-bold text-gray-900">
                SAR {stats.totalProcurementValue.toFixed(2)}
              </div>
              <div className="text-xs text-indigo-600 font-medium mt-1">
                SAR {stats.pendingVatAmount.toFixed(2)} {t('VAT (15%)')}
              </div>
            </div>
          </div>

          {/* Recent Invoices Table */}
          <div className="bg-white p-4 rounded-lg shadow-sm space-y-3">
            <div className="flex justify-between items-center border-b pb-2">
              <h3 className="font-semibold text-gray-800 text-base">{t('Recent Purchase Invoices')}</h3>
              <button
                onClick={() => navigate('/manager/erp/purchase-invoices')}
                className="text-xs text-primary font-medium hover:underline"
              >
                {t('View All Invoices →')}
              </button>
            </div>

            <CustomTable
              data={recentInvoices}
              isLoading={loading}
              loadingIcon={<TableLoading />}
              columns={columnsData}
            />
          </div>
        </>
      )}
    </div>
  );
}
